import { createContext, useContext, useState, useEffect } from "react";

const AttendeeContext = createContext();

export function AttendeeProvider({ children }) {
  const [attendee, setAttendee] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get current event slug from URL
  const getCurrentEventSlug = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    return pathParts[1]; // /e/:eventSlug format
  };

  // Load attendee data from localStorage on mount
  useEffect(() => {
    const loadAttendee = () => {
      try {
        const eventSlug = getCurrentEventSlug();

        if (!eventSlug) {
          setAttendee(null);
          setLoading(false);
          return;
        }

        const sessionKey = `attendee_session_${eventSlug}`;
        let session = localStorage.getItem(sessionKey);

        // Check for preserved auth data if session is not found
        if (!session) {
          const preservedData = sessionStorage.getItem("preserved_auth_data");
          if (preservedData) {
            try {
              const authData = JSON.parse(preservedData);
              if (authData[sessionKey]) {
                localStorage.setItem(sessionKey, authData[sessionKey]);
                session = authData[sessionKey];
                // Clean up preserved data after restoring
                delete authData[sessionKey];
                if (Object.keys(authData).length === 0) {
                  sessionStorage.removeItem("preserved_auth_data");
                } else {
                  sessionStorage.setItem(
                    "preserved_auth_data",
                    JSON.stringify(authData)
                  );
                }
              }
            } catch (error) {}
          }
        }

        if (session) {
          const sessionData = JSON.parse(session);

          // Validate that the session has required data
          if (sessionData.attendee && sessionData.session_token) {
            setAttendee(sessionData.attendee);
          } else {
            // Invalid session, clear it
            localStorage.removeItem(sessionKey);
            setAttendee(null);
          }
        } else {
          setAttendee(null);
        }
      } catch (error) {
        // Clear invalid session data
        const eventSlug = getCurrentEventSlug();
        if (eventSlug) {
          localStorage.removeItem(`attendee_session_${eventSlug}`);
        }
        setAttendee(null);
      } finally {
        setLoading(false);
      }
    };

    loadAttendee();

    // Listen for storage changes (when session is updated from another tab/context)
    const handleStorageChange = (e) => {
      const eventSlug = getCurrentEventSlug();
      if (eventSlug && e.key === `attendee_session_${eventSlug}`) {
        loadAttendee();
      }
    };

    // Listen for custom events (for same-tab updates)
    const handleSessionUpdate = () => {
      loadAttendee();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("attendee_session_updated", handleSessionUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "attendee_session_updated",
        handleSessionUpdate
      );
    };
  }, []);

  // Update attendee data
  const updateAttendee = (newAttendeeData) => {
    setAttendee(newAttendeeData);

    // Also update localStorage
    try {
      const eventSlug = getCurrentEventSlug();
      if (eventSlug) {
        const sessionKey = `attendee_session_${eventSlug}`;
        const session = localStorage.getItem(sessionKey);
        if (session) {
          const sessionData = JSON.parse(session);
          const updatedSession = {
            ...sessionData,
            attendee: newAttendeeData,
          };
          localStorage.setItem(sessionKey, JSON.stringify(updatedSession));
        }
      }
    } catch (error) {}
  };

  // Clear attendee data (for logout)
  const clearAttendee = () => {
    setAttendee(null);
    const eventSlug = getCurrentEventSlug();
    if (eventSlug) {
      localStorage.removeItem(`attendee_session_${eventSlug}`);
    }
  };

  // Check if profile is complete
  const isProfileComplete = () => {
    return attendee && attendee.first_name && attendee.last_name;
  };

  // Helper function to get attendee initials
  const getAttendeeInitials = () => {
    if (!attendee) return "U";
    const firstName = attendee.first_name || "";
    const lastName = attendee.last_name || "";
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || "U";
  };

  // Helper function to get attendee full name
  const getAttendeeName = () => {
    if (!attendee) return "User";
    const firstName = attendee.first_name || "";
    const lastName = attendee.last_name || "";
    return `${firstName} ${lastName}`.trim() || "User";
  };

  const value = {
    attendee,
    loading,
    updateAttendee,
    clearAttendee,
    isProfileComplete,
    getAttendeeInitials,
    getAttendeeName,
  };

  return (
    <AttendeeContext.Provider value={value}>
      {children}
    </AttendeeContext.Provider>
  );
}

export function useAttendee() {
  const context = useContext(AttendeeContext);
  if (context === undefined) {
    throw new Error("useAttendee must be used within an AttendeeProvider");
  }
  return context;
}
